/**
 * section-tracking.js
 * Fenge Section Level Analytics - Phase 2 Advanced Module
 *
 * 负责追踪:
 *   - section_view : Section 进入视口 50% 并停留 0.8s
 *   - section_click: Section 内的链接/按钮点击
 *   - block_view   : 具体卡片 (Product/Blog/Slide) 进入视口 50%
 *   - block_click  : 具体卡片被点击
 *   - scroll_depth : 滚动到新 Section 时记录深度
 *   - expand       : FAQ / 折叠内容展开
 *
 * 原理: 在 Theme (主线程) 采集数据 → analytics.publish() → Custom Pixel 沙盒 → GA4
 * 
 * @version 2.0.0
 * @changelog 2.0.0 — Phase 2: block_view/block_click 完整实现，导航按钮过滤优化
 */

(function () {
  'use strict';

  // ─── 工具函数 ────────────────────────────────────────────────────────────────

  /**
   * 向 Shopify Custom Pixel 沙盒发布自定义事件
   * @param {string} eventName  - 事件名称 (snake_case)
   * @param {object} eventData  - 事件参数
   */
  function publishEvent(eventName, eventData) {
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
      return;
    }
    try {
      window.Shopify.analytics.publish(eventName, eventData);
    } catch (err) {
      // 生产环境静默失败
    }
  }

  /**
   * 获取 Section 的 ID
   */
  function getSectionId(el) {
    const wrapper = el.closest('[id^="shopify-section-"]');
    if (wrapper) {
      return wrapper.id.replace('shopify-section-', '');
    }
    return el.id || el.dataset.sectionId || '';
  }

  /**
   * 获取 Section 类型
   */
  function getSectionType(el) {
    const wrapper = el.closest('[id^="shopify-section-"]');
    if (!wrapper) return 'unknown';
    const classList = Array.from(wrapper.classList);
    const sectionClass = classList.find(
      (c) => c !== 'shopify-section' && !c.startsWith('shopify-section--')
    );
    return sectionClass || 'section';
  }

  // ─── 全局状态 ────────────────────────────────────────────────────────────────

  const viewedSections = new Set();
  const viewedBlocks = new Set();
  const scrollDepthReported = new Set();

  // ─── Section View 追踪 ────────────────────────────────────────────────────────

  function initSectionViewTracking() {
    const sections = document.querySelectorAll('[id^="shopify-section-"]');
    if (!sections.length) return;

    const sectionList = Array.from(sections);
    sectionList.forEach((el, idx) => {
      el.dataset.sectionIndex = idx;
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          const sectionId = getSectionId(el);

          if (viewedSections.has(sectionId)) return;

          const timer = setTimeout(() => {
            if (entry.isIntersecting && !viewedSections.has(sectionId)) {
              viewedSections.add(sectionId);
              const idx = parseInt(el.dataset.sectionIndex || '0', 10);

              publishEvent('section_view', {
                section_id: sectionId,
                section_type: getSectionType(el),
                section_index: idx,
                page_path: window.location.pathname,
              });

              trackScrollDepth(el, idx);
            }
          }, 800);

          // 离开监听
          const leaveObserver = new IntersectionObserver(
            (leaveEntries) => {
              leaveEntries.forEach((leaveEntry) => {
                if (!leaveEntry.isIntersecting) {
                  clearTimeout(timer);
                  leaveObserver.disconnect();
                }
              });
            },
            { threshold: 0.5 }
          );
          leaveObserver.observe(el);
        });
      },
      { threshold: 0.5, rootMargin: '0px' }
    );

    sections.forEach((el) => observer.observe(el));
  }

  // ─── Block View 追踪 (卡片/幻灯片) ─────────────────────────────────────────────

  function initBlockViewTracking() {
    // 自动识别卡片类元素
    const blockSelectors = [
      '[data-track-block]'
    ];
    
    const blocks = document.querySelectorAll(blockSelectors.join(','));
    if (!blocks.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          const sectionWrapper = el.closest('[id^="shopify-section-"]');
          const sectionId = sectionWrapper ? sectionWrapper.id.replace('shopify-section-', '') : 'unknown';
          
          // 生成 Block 唯一标识: sectionId + (blockId 或 index)
          const blockId = el.dataset.blockId || el.id || '';
          const blockIndex = el.dataset.blockIndex || '';
          const viewKey = `${sectionId}-${blockId}-${blockIndex}`;

          if (viewedBlocks.has(viewKey)) return;

          // 停留 500ms 算曝光
          const timer = setTimeout(() => {
            if (entry.isIntersecting && !viewedBlocks.has(viewKey)) {
              viewedBlocks.add(viewKey);
              
              publishEvent('block_view', {
                section_id: sectionId,
                section_type: getSectionType(el),
                block_id: blockId,
                block_type: el.dataset.blockType || 'card',
                block_title: el.dataset.blockTitle || el.querySelector('h3, h2, .product-card__title')?.textContent?.trim() || '',
                block_index: parseInt(blockIndex || '0', 10),
                page_path: window.location.pathname,
              });
            }
          }, 500);

          const leaveObserver = new IntersectionObserver(
            (leaveEntries) => {
              leaveEntries.forEach((leaveEntry) => {
                if (!leaveEntry.isIntersecting) {
                  clearTimeout(timer);
                  leaveObserver.disconnect();
                }
              });
            },
            { threshold: 0.5 }
          );
          leaveObserver.observe(el);
        });
      },
      { threshold: 0.5, rootMargin: '0px' }
    );

    blocks.forEach((el) => observer.observe(el));
  }

  // ─── Click 追踪 ──────────────────────────────────────────────────────────────

  function initClickTracking() {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest('a, button');
        if (!target) return;

        const sectionWrapper = target.closest('[id^="shopify-section-"]');
        if (!sectionWrapper) return;

        const sectionId = sectionWrapper.id.replace('shopify-section-', '');
        const blockWrapper = target.closest('[data-track-block]');

        const commonData = {
          section_id: sectionId,
          section_type: getSectionType(sectionWrapper),
          page_path: window.location.pathname,
        };

        // 如果在 Block 内点击，触发 block_click
        if (blockWrapper) {
          publishEvent('block_click', {
            ...commonData,
            block_id: blockWrapper.dataset.blockId || blockWrapper.id || '',
            block_type: blockWrapper.dataset.blockType || 'card',
            block_title: blockWrapper.dataset.blockTitle || blockWrapper.querySelector('h3, h2, .product-card__title')?.textContent?.trim() || '',
            block_index: parseInt(blockWrapper.dataset.blockIndex || '0', 10),
            click_url: target.getAttribute('href') || target.dataset.href || '',
          });
        }

        // 同时保留基础的 section_click (用于分析整体转化)
        const clickText = (target.textContent || target.getAttribute('aria-label') || '').trim().slice(0, 100);
        // 导航控制按钮排除: previous-button / next-button / 左右箭头导航按钮
        // 不排除 Quick View 按钮 / Accordion 按钮 / 加购按钮
        const isNavControl = 
          target.getAttribute('is') === 'previous-button' ||
          target.getAttribute('is') === 'next-button' ||
          (target.getAttribute('aria-controls') && (
            target.getAttribute('aria-controls').startsWith('Slider-') ||
            target.getAttribute('aria-controls').startsWith('JnlScroll-')
          ));

        if (!isNavControl) {
          publishEvent('section_click', {
            ...commonData,
            click_text: clickText,
            click_url: target.getAttribute('href') || target.dataset.href || '',
          });
        }
      },
      { passive: true }
    );
  }

  // ─── Scroll Depth 追踪 ────────────────────────────────────────────────────────

  function trackScrollDepth(el, sectionIdx) {
    const key = `${sectionIdx}`;
    if (scrollDepthReported.has(key)) return;
    scrollDepthReported.add(key);

    const pageHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, 1);
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const viewportHeight = window.innerHeight || 0;
    const pct = Math.min(100, Math.round(((scrollTop + viewportHeight) / pageHeight) * 100));

    publishEvent('scroll_depth', {
      section_index: sectionIdx,
      section_id: el.id.replace('shopify-section-', ''),
      pct: pct,
      page_path: window.location.pathname,
    });
  }

  // ─── Expand (FAQ) 追踪 ───────────────────────────────────────────────────────

  function initExpandTracking() {
    document.addEventListener('click', (event) => {
      const summary = event.target.closest('summary');
      if (!summary) return;
      const details = summary.closest('details');
      if (!details || details.open) return;

      const sectionWrapper = details.closest('[id^="shopify-section-"]');
      if (!sectionWrapper) return;

      publishEvent('expand', {
        section_id: sectionWrapper.id.replace('shopify-section-', ''),
        expand_title: (summary.textContent || '').trim().slice(0, 150),
        page_path: window.location.pathname,
      });
    }, { passive: true });
  }

  // ─── 初始化 ──────────────────────────────────────────────────────────────────

  function init() {
    if (document.documentElement.classList.contains('shopify-design-mode') || window.Shopify?.designMode) return;

    initSectionViewTracking();
    initBlockViewTracking();
    initClickTracking();
    initExpandTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // 处理动态加载
  document.addEventListener('shopify:section:load', init);
})();
